import {
  CreatePackagePayload,
  CreditPackagePayload,
  GetBundleByStoreAndPackageIdResponse,
  GetBundleUnitsByStoreAndPackageIdResponse,
  GetCorePackageApiResponse,
  GetCorePricingApiResponse,
  GetCreditPackageApiResponse,
  GetPackageByIdApiResponse,
  GetPackageByStoreApiResponse,
  PortionListApiResponse,
  PostBundleApiResponse,
  PostBundlePayload,
  PostBundleUnitApiResponse,
  PostCorePricingApiResponse,
  PostCreditPackageApiResponse,
  UpdateMacroMealsPayload,
} from "app/types/package";
import axios from "./config";
import { AxiosResponse } from "axios";
import { BundleUnitPayload } from "app/types/common";

export const getPackagesListApi = (
  storeId: number | string,
  query: string,
): Promise<AxiosResponse<GetPackageByStoreApiResponse>> =>
  axios.get<GetPackageByStoreApiResponse>(
    `/stores/${storeId}/packages${query}`,
  );
export const getPackagesByIdApi = (
  storeId: number,
  id: number,
): Promise<AxiosResponse<GetPackageByIdApiResponse>> =>
  axios.get<GetPackageByIdApiResponse>(`/stores/${storeId}/packages/${id}`);

export const postPackagesApi = (
  storeId: number,
  payload: CreatePackagePayload,
) => {
  return axios.post(`/stores/${storeId}/packages`, payload);
};

export const putPackagesApi = (
  storeId: number,
  packageId: number,
  payload: CreatePackagePayload,
) => {
  return axios.put(`/stores/${storeId}/packages/${packageId}`, payload);
};

// Bundles API

type BundlePostRequest = {
  packageId: number;
  storeId: number;
  payload: PostBundlePayload;
};

export const postBundlesApi = ({
  packageId,
  storeId,
  payload,
}: BundlePostRequest): Promise<AxiosResponse<PostBundleApiResponse>> => {
  return axios.post<PostBundleApiResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles`,
    payload,
  );
};

type BundlePutRequest = {
  packageId: number;
  bundleId: number;
  storeId: number;
  payload: PostBundlePayload;
};

export const putBundleApi = ({
  packageId,
  bundleId,
  storeId,
  payload,
}: BundlePutRequest): Promise<AxiosResponse<PostBundleApiResponse>> => {
  return axios.put<PostBundleApiResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles/${bundleId}`,
    payload,
  );
};

type GetBundleByStoreAndPackageIdRequest = {
  storeId: number;
  packageId: number;
  query: string;
};
export const getBundleByStoreAndPackageId = ({
  storeId,
  packageId,
  query,
}: GetBundleByStoreAndPackageIdRequest): Promise<
  AxiosResponse<GetBundleByStoreAndPackageIdResponse>
> => {
  return axios.get<GetBundleByStoreAndPackageIdResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles${query}`,
  );
};

// Bundle Units API

type GetBundleUnitsByStoreAndPackageIdRequest = {
  storeId: number;
  packageId: number;
  bundleId: number;
  query: string;
};
export const getBundleUnitsByStoreAndPackageId = ({
  storeId,
  packageId,
  bundleId,
  query,
}: GetBundleUnitsByStoreAndPackageIdRequest): Promise<
  AxiosResponse<GetBundleUnitsByStoreAndPackageIdResponse>
> => {
  return axios.get<GetBundleUnitsByStoreAndPackageIdResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles/${bundleId}/units${query}`,
  );
};

type BundleUnitPostRequest = {
  storeId: number;
  packageId: number;
  bundleId: number;
  payload: BundleUnitPayload;
};
export const postBundleUnitsApi = ({
  storeId,
  packageId,
  bundleId,
  payload,
}: BundleUnitPostRequest): Promise<
  AxiosResponse<PostBundleUnitApiResponse>
> => {
  return axios.post<PostBundleUnitApiResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles/${bundleId}/units`,
    payload,
  );
};

export const putBundleUnitApi = ({
  storeId,
  packageId,
  bundleId,
  unitId,
  payload,
}: BundleUnitPostRequest & { unitId: number }): Promise<
  AxiosResponse<PostBundleUnitApiResponse>
> => {
  return axios.put<PostBundleUnitApiResponse>(
    `/stores/${storeId}/packages/${packageId}/bundles/${bundleId}/units/${unitId}`,
    payload,
  );
};

export const getAllBundleUnitsApi = (storeId: number | string, query: string) =>
  axios.get(`/stores/${storeId}/units${query}`);

// Credit Packages

export const getCreditPackagesApi = ({
  storeId,
  packageId,
  query,
}: {
  storeId: number;
  packageId: number;
  query: string;
}): Promise<AxiosResponse<GetCreditPackageApiResponse>> => {
  return axios.get<GetCreditPackageApiResponse>(
    `/stores/${storeId}/packages/${packageId}/credit-packages${query}`,
  );
};

export const postCreditPackagesApi = ({
  storeId,
  packageId,
  payload,
}: {
  storeId: number;
  packageId: number;
  payload: CreditPackagePayload;
}): Promise<AxiosResponse<PostCreditPackageApiResponse>> => {
  return axios.post<PostCreditPackageApiResponse>(
    `/stores/${storeId}/packages/${packageId}/credit-packages`,
    payload,
  );
};

export const putCreditPackagesApi = ({
  storeId,
  packageId,
  creditPackageId,
  payload,
}: {
  storeId: number;
  packageId: number;
  creditPackageId: number;
  payload: CreditPackagePayload;
}): Promise<AxiosResponse<PostCreditPackageApiResponse>> => {
  return axios.put<PostCreditPackageApiResponse>(
    `/stores/${storeId}/packages/${packageId}/credit-packages/${creditPackageId}`,
    payload,
  );
};

export const getPortionsApi = (
  brandId: number,
  query: string,
): Promise<AxiosResponse<PortionListApiResponse>> =>
  axios.get<PortionListApiResponse>(`/brands/${brandId}/portions${query}`);

export const getCoreMacrosOptionsAPI = (
  storeId: number,
): Promise<AxiosResponse<GetCorePackageApiResponse>> =>
  axios.get<GetCorePackageApiResponse>(`/stores/${storeId}/macros-options`);

/**
 * @deprecated Use postCoreMacrosPricingAPI instead
 * @param storeId - The store ID
 * @param query - The query string
 * @returns A promise that resolves to the response from the API
 */
export const getCoreMacrosPricingAPI = (
  storeId: number,
  query = "",
): Promise<AxiosResponse<GetCorePricingApiResponse>> =>
  axios.get<GetCorePricingApiResponse>(
    `/stores/${storeId}/macros-pricing${query}`,
  );

export const postCoreMacrosPricingAPI = (
  storeId: number,
  payload: PostCorePricingApiResponse,
): Promise<AxiosResponse<GetCorePricingApiResponse>> =>
  axios.post<GetCorePricingApiResponse>(
    `/stores/${storeId}/macros-pricing`,
    payload,
  );

export const updateMacroMealsApi = (
  subscriptionId: number,
  payload: UpdateMacroMealsPayload,
) => {
  return axios.put(`subscriptions/${subscriptionId}/macro-meals`, payload);
};
